import {
  faBell,
  faChartLine,
  faChevronUp,
  faCogs,
  faKey,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, Link, Menu, Skeleton, Stack, useLocation } from 'shared';
import { LetterIcon, useBreakpoints } from 'App';
import { useUser } from 'Auth';
import { buildRedirectUri } from 'Auth/redirectUri';
import React, { useState } from 'react';
import { useSentryUser } from 'Users/hooks/SentryUserProvider';
import { UserType } from 'Users/userTypes';
import styles from './UserDropdown.module.css';

interface DropdownPanelProps {
  linkTo: 'frontoffice' | 'backoffice';
}

const DropdownPanel: React.FC<DropdownPanelProps> = ({ linkTo }) => {
  const { pathname, search } = useLocation();
  const { user } = useSentryUser();

  const menuItems = [
    ...(linkTo === 'frontoffice'
      ? [
          {
            key: 'switchLink',
            className: styles.userMenuItem,
            label: (
              <>
                <FontAwesomeIcon
                  icon={faChartLine}
                  size="lg"
                  className={styles.userMenuItemIcon}
                />
                Front Office
                <Link to="/dashboard" />
              </>
            ),
          },
        ]
      : []),
    ...(linkTo === 'backoffice' && user && user.userTypeId !== UserType.Customer
      ? [
          {
            key: 'switchLink',
            className: styles.userMenuItem,
            label: (
              <>
                <FontAwesomeIcon
                  icon={faCogs}
                  size="lg"
                  className={styles.userMenuItemIcon}
                />
                Back Office
                <Link to="/" />
              </>
            ),
          },
        ]
      : []),
    {
      key: 'userNotifications',
      className: styles.userMenuItem,
      label: (
        <>
          <FontAwesomeIcon
            icon={faBell}
            size="lg"
            className={styles.userMenuItem}
          />
          User Notification Settings
          <Link
            to={
              user?.userTypeId === 5
                ? `/dashboard/user/${user?.id}/notifications`
                : `/user/${user?.id}/notifications`
            }
          />
        </>
      ),
    },
    {
      key: 'resetPassword',
      className: styles.userMenuItem,
      label: (
        <>
          <FontAwesomeIcon
            icon={faKey}
            size="lg"
            className={styles.userMenuItemIcon}
          />
          Reset Password
          <Link to={buildRedirectUri('/reset-password', pathname, search)} />
        </>
      ),
    },
    {
      key: 'logout',
      className: styles.userMenuItem,
      label: (
        <>
          <FontAwesomeIcon
            icon={faSignOutAlt}
            size="lg"
            className={styles.userMenuItemIcon}
          />
          Logout
          <Link to="/logout" />
        </>
      ),
    },
  ];

  return <Menu className={'menu'} selectedKeys={[]} items={menuItems} />;
};

export interface UserDropdownProps {
  linkTo: 'frontoffice' | 'backoffice';
}

export const UserDropdown: React.FC<UserDropdownProps> = ({ linkTo }) => {
  const user = useUser();
  const username = user?.name;

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { smd } = useBreakpoints();

  return (
    <Dropdown
      popupRender={() => <DropdownPanel linkTo={linkTo} />}
      open={username !== undefined && showUserDropdown}
      onOpenChange={(v) => username && setShowUserDropdown(v)}
    >
      {/* This needs a div to wrap it since dropdown injects into its onHover which Stack doesn't have */}
      <div>
        <Stack className="userDropdown" gap={8} alignment="middle">
          <LetterIcon name={username ?? '??'} small />
          {!username && smd && <Skeleton />}
          {username && smd && (
            <span className={styles.userLabel}>{username}</span>
          )}
          <FontAwesomeIcon
            className={styles.smoothIcon}
            icon={faChevronUp}
            rotation={showUserDropdown ? 180 : undefined}
          />
        </Stack>
      </div>
    </Dropdown>
  );
};
